import React from 'react'
import { Grid, GridColumn, GridDataStateChangeEvent } from '@progress/kendo-react-grid'
import { Button } from '@progress/kendo-react-buttons'
import { Switch } from '@progress/kendo-react-inputs'
import { Tooltip } from '@progress/kendo-react-tooltip'
import '@progress/kendo-theme-fluent/dist/all.css'
import './JobsKendoGrid.css'
import { 
  FaCheckCircle, 
  FaTimesCircle, 
  FaPlusCircle,
  FaFileExcel,
  FaStickyNote
} from 'react-icons/fa'
import { jobsGrid } from '../../data/interfaces'

interface JobsKendoGridProps {
  data: {
    data: jobsGrid[]
    total: number
  }
  loading: boolean
  onDataStateChange: (event: GridDataStateChangeEvent) => void
  lockEdit: boolean
  showCompleted: boolean
  showDeleted: boolean
  showEngineerComments: boolean
  showRemedialWorks: boolean
  onShowCompleted: (event: any) => void
  onShowDeleted: (event: any) => void
  onShowEngineerComments: (event: any) => void
  onShowRemedialWorks: (event: any) => void
  onAddToggle: () => void
  onEditToggle: (jobId: number) => void
  onJobComplete: (jobId: number) => void
  onJobDelete: (jobId: number) => void
  onExport: () => void
}

const JobsKendoGrid: React.FC<JobsKendoGridProps> = (props) => {

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB')
  }

  const formatCurrency = (amount: number) => {
    if (!amount) return '£0.00'
    return `£${amount.toFixed(2)}`
  }

  return (
    <div className="jobs-kendo-fluent-theme">
      {/* Controls Header */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginBottom: '24px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        border: '1px solid #edebe9'
      }}>
        <h2 style={{ 
          fontSize: '1.25rem', 
          fontWeight: '600',
          margin: 0,
          color: '#323130'
        }}>
          Jobs
        </h2>
        
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '24px',
          alignItems: 'center'
        }}>
        
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: "8px",
          minWidth: "200px"
        }}>
          <label style={{ 
            fontSize: "14px",
            color: "#605e5c",
            fontWeight: "400",
            whiteSpace: "nowrap"
          }}>
            Show Completed:
          </label>
          <Switch
            onChange={props.onShowCompleted}
            checked={props.showCompleted}
          />
          <Tooltip anchorElement="target" position="top" openDelay={0}>
            <FaCheckCircle
              style={{
                fontSize: "16px",
                color: props.showCompleted ? "#0078d4" : "#8a8886"
              }}
              title="Show Completed Jobs?"
            />
          </Tooltip>
        </div>

        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: "8px",
          minWidth: "180px"
        }}>
          <label style={{ 
            fontSize: "14px",
            color: "#605e5c",
            fontWeight: "400",
            whiteSpace: "nowrap"
          }}>
            Show Deleted:
          </label>
          <Switch
            onChange={props.onShowDeleted}
            checked={props.showDeleted}
          />
          <Tooltip anchorElement="target" position="top" openDelay={0}>
            <FaTimesCircle
              style={{
                fontSize: "16px",
                color: props.showDeleted ? "#d13438" : "#8a8886"
              }}
              title="Show Deleted Jobs?"
            />
          </Tooltip>
        </div>

        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: "8px",
          minWidth: "220px"
        }}>
          <label style={{ 
            fontSize: "14px",
            color: "#605e5c",
            fontWeight: "400",
            whiteSpace: "nowrap"
          }}>
            Engineer Comments:
          </label>
          <Switch
            onChange={props.onShowEngineerComments}
            checked={props.showEngineerComments}
          />
          <Tooltip anchorElement="target" position="top" openDelay={0}>
            <FaStickyNote
              style={{
                fontSize: "16px",
                color: props.showEngineerComments
                  ? "#ca5010"
                  : "#8a8886",
              }}
              title="Show Engineer Comments?"
            />
          </Tooltip>
        </div>

        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: "8px",
          minWidth: "200px"
        }}>
          <label style={{ 
            fontSize: "14px",
            color: "#605e5c",
            fontWeight: "400",
            whiteSpace: "nowrap"
          }}>
            Remedial Works:
          </label>
          <Switch
            onChange={props.onShowRemedialWorks}
            checked={props.showRemedialWorks}
          />
          <Tooltip anchorElement="target" position="top" openDelay={0}>
            <FaCheckCircle
              style={{
                fontSize: "16px",
                color: props.showRemedialWorks
                  ? "#107c10"
                  : "#8a8886",
              }}
              title="Show Remedial Works?"
            />
          </Tooltip>
        </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        marginBottom: "1rem"
      }}>
        <div style={{ display: "flex", gap: "1rem" }}>
          {!props.lockEdit && (
            <Button
              themeColor="primary"
              onClick={props.onAddToggle}
            >
              <FaPlusCircle style={{ marginRight: "0.5rem" }} />
              Add New Job
            </Button>
          )}
        </div>
        
        <Button
          onClick={props.onExport}
          fillMode="outline"
        >
          <FaFileExcel style={{ marginRight: "0.5rem" }} />
          Export to Excel
        </Button>
      </div>

      {/* Kendo Grid */}
      <Grid
        data={props.data}
        pageable={{
          buttonCount: 5,
          info: true,
          type: 'numeric',
          pageSizes: [10, 25, 50, 100],
          previousNext: true
        }}
        sortable={true}
        filterable={true}
        onDataStateChange={props.onDataStateChange}
        resizable={true}
        reorderable={true}
        style={{ 
          height: '600px'
        }}
      >
        <GridColumn 
          field="refFormatted" 
          title="Job No" 
          width="120px"
          filterable={true}
          sortable={true}
        />
        
        <GridColumn 
          field="client" 
          title="Client" 
          width="200px"
          filterable={true}
          sortable={true}
        />
        
        <GridColumn 
          field="site" 
          title="Site" 
          width="250px"
          filterable={true}
          sortable={true}
        />
        
        <GridColumn 
          field="description" 
          title="Description" 
          width="300px"
          filterable={true}
          sortable={true}
        />
        
        <GridColumn 
          field="worktype" 
          title="Type of Work" 
          width="150px"
          filterable={true}
          sortable={true}
        />
        
        <GridColumn 
          field="createdDate" 
          title="Date Created" 
          width="120px"
          filter="date"
          format="{0:dd/MM/yyyy}"
          sortable={true}
          cell={({ dataItem }) => (
            <td>{formatDate(dataItem.createdDate)}</td>
          )}
        />
        
        <GridColumn 
          field="deadDate" 
          title="Dead Date" 
          width="120px"
          filter="date"
          format="{0:dd/MM/yyyy}"
          sortable={true}
          cell={({ dataItem }) => (
            <td>{formatDate(dataItem.deadDate)}</td>
          )}
        />
        
        <GridColumn 
          field="employeeProjectmanager" 
          title="Assigned To" 
          width="150px"
          filterable={true}
          sortable={true}
        />
        
        <GridColumn 
          field="priority" 
          title="Priority" 
          width="100px"
          filterable={true}
          sortable={true}
        />
        
        <GridColumn 
          field="likelihood" 
          title="Likelihood" 
          width="100px"
          filterable={true}
          sortable={true}
        />
        
        <GridColumn 
          field="quotedValue" 
          title="Quoted Value" 
          width="120px"
          sortable={true}
          cell={({ dataItem }) => (
            <td style={{ textAlign: 'right' }}>
              {formatCurrency(dataItem.quotedValue)}
            </td>
          )}
        />
      </Grid>

      {/* Summary */}
      <div style={{ 
        marginTop: '24px', 
        padding: '16px', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '2px',
        border: '1px solid #edebe9',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ 
          fontSize: '14px',
          fontWeight: '600',
          color: '#323130'
        }}>
          Total Records: <span style={{ color: '#0078d4' }}>{props.data.total}</span>
        </div>
        <div style={{ 
          fontSize: '12px', 
          color: '#605e5c',
          fontStyle: 'italic'
        }}>
          ✨ Server-side filtering and pagination enabled
        </div>
      </div>
    </div>
  )
}

export default JobsKendoGrid